import { useEffect, useMemo, useState } from "react";
import { Sparkles, RefreshCw, TrendingUp, Users, Zap, ChevronDown, ChevronUp, Package, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import InfoTip from "./InfoTip";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const fmt1 = (n) => new Intl.NumberFormat("es-DO", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n || 0);

const RISK = {
  high: { color: "#dc2626", bg: "#fee2e2", label: "RIESGO ALTO" },
  med:  { color: "#d97706", bg: "#fef3c7", label: "MEDIO" },
  low:  { color: "#16a34a", bg: "#dcfce7", label: "OK" },
};

const KPI_TIPS = {
  sales: "Cantidad de ventas que el modelo proyecta lograr en los próximos meses, considerando tu histórico + días laborables + inventario disponible.",
  leads_needed: "Cantidad de leads que necesitas captar para lograr las ventas proyectadas. Se calcula: ventas ÷ tasa de conversión actual.",
  conversion: "Tasa promedio proyectada de lead → venta. Benchmark saludable: 12-15%.",
  target: "Meta comercial cargada desde el Plan Anual (Presupuesto V2). Cumplimiento % = Proyección ÷ Meta. Si el modelo proyecta menos que la meta, existe un GAP que hay que cerrar.",
  coverage: "Meses de stock disponible según el ritmo de ventas proyectado. Verde ≥2m · Ámbar 1-2m · Rojo <1m.",
  mape: "Diferencia promedio entre lo que el modelo predijo y lo que realmente pasó, medido con datos del último año. Cuanto más bajo, más confiable la proyección.",
  confidence: "En 8 de cada 10 casos, el valor real caerá dentro del rango proyectado (intervalo de confianza del 80%).",
};

/**
 * Fase 2 · Executive Sales Forecast Panel (SARIMAX Nivel 1+2).
 *
 * Commercial-decision view: focuses on *ventas próximas*, *leads necesarios*
 * y *cobertura de inventario*. Model precision is tucked into a slim strip
 * at the bottom (with tooltips) so the operator sees business numbers first.
 */
export default function SarimaPanel({ stockAlerts = [], renderAlertRow }) {
  const [data, setData] = useState(null);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [horizon, setHorizon] = useState(3);
  const [expandBrands, setExpandBrands] = useState(false);
  const [expandAlerts, setExpandAlerts] = useState(false);

  const run = (force = false) => {
    setLoading(true);
    const url = `/predictive/sarima-summary?horizon=${horizon}&min_year=2020${force ? "&force_refresh=true" : ""}`;
    Promise.all([
      api.get(url),
      api.get(`/predictive/targets/summary?horizon=${horizon}`),
    ])
      .then(([sarima, tgt]) => {
        setData(sarima.data);
        setTargets(tgt.data);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [horizon]);

  const salesFC = data?.total_sales?.forecast || [];
  const leadsFC = data?.leads?.forecast || [];
  const convFC = data?.conversion?.forecast || [];

  const totalSalesNext = useMemo(() => salesFC.reduce((s, p) => s + (p.mean || 0), 0), [salesFC]);
  const convAvg = useMemo(() => {
    if (!convFC.length) return null;
    return convFC.reduce((s, x) => s + x.mean, 0) / convFC.length;
  }, [convFC]);
  // Leads NEEDED to hit projected sales at projected conversion rate.
  // Convert conversion to decimal, guard div-by-zero.
  const leadsNeededNext = useMemo(() => {
    if (!convAvg || convAvg <= 0) return 0;
    return totalSalesNext / (convAvg / 100);
  }, [totalSalesNext, convAvg]);

  // Per-month "leads needed" derived from per-month sales forecast + avg conv.
  const leadsNeededByMonth = useMemo(() => {
    if (!convAvg || convAvg <= 0) return [];
    return salesFC.map((p) => ({ ...p, mean: p.mean / (convAvg / 100) }));
  }, [salesFC, convAvg]);

  return (
    <section className="surface p-6 mb-5" data-testid="sarima-panel">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} style={{ color: "#8b5cf6" }}/>
            <h2 className="text-lg font-extrabold font-display">Forecast Ejecutivo</h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold"
                  style={{ background: "#ede9fe", color: "#5b21b6" }}>PROYECCIÓN A {horizon} MESES</span>
          </div>
          <p className="text-xs text-gray-500 font-semibold">
            Ventas proyectadas · Leads que necesitas · Cobertura de inventario por marca.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
            Horizonte
            <select value={horizon} onChange={(e) => setHorizon(Number(e.target.value))}
                    className="text-xs font-extrabold px-2 py-1 rounded border border-gray-200 bg-white"
                    data-testid="sarima-horizon">
              {[1, 2, 3, 4, 5, 6].map((h) => <option key={h} value={h}>{h}m</option>)}
            </select>
          </label>
          <button onClick={() => run(true)} disabled={loading}
                  className="px-3 py-1.5 rounded-lg font-extrabold text-xs flex items-center gap-1.5 disabled:opacity-40"
                  style={{ background: "#111827", color: "#f4c400" }}
                  data-testid="sarima-refresh"
                  title="Fuerza el recálculo ignorando la caché (30 min TTL). Tarda 5-15s.">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""}/>
            {loading ? "Calculando…" : "Re-calcular"}
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-10 text-sm font-semibold text-gray-400">
          Calculando proyecciones — 5-15 segundos…
        </div>
      )}

      {data && (
        <>
          {/* Meta status banner — always visible when we have targets.
              Rojo/ámbar cuando cumplimiento < 85% (headline crítico) o verde
              sutil cuando ≥ 85% (confirma que el pipeline va bien). */}
          {targets?.total_horizon > 0 && (() => {
            const pct = (totalSalesNext / targets.total_horizon) * 100;
            const ok = pct >= 100;
            const success = pct >= 85 && pct < 100;
            const critical = pct < 70;
            let bg, border, color, title, icon;
            if (ok) {
              bg = "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)";
              border = "#a7f3d0"; color = "#065f46";
              title = "Meta cumplida (proyección)";
              icon = <TrendingUp size={22} strokeWidth={2.4}/>;
            } else if (success) {
              bg = "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)";
              border = "#a7f3d0"; color = "#065f46";
              title = "Meta en buen camino";
              icon = <TrendingUp size={22} strokeWidth={2.4}/>;
            } else if (critical) {
              bg = "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)";
              border = "#fecaca"; color = "#991b1b";
              title = "Meta en riesgo alto";
              icon = <AlertTriangle size={22} strokeWidth={2.4}/>;
            } else {
              bg = "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)";
              border = "#fde68a"; color = "#92400e";
              title = "Meta bajo cumplimiento esperado";
              icon = <AlertTriangle size={22} strokeWidth={2.4}/>;
            }
            const pillBg = critical ? "#fee2e2"
                          : (ok || success) ? "#d1fae5"
                          : "#fef3c7";
            // Rank underperforming brands only (relevant to risk states).
            const risky = (data.brands || [])
              .map((b) => {
                const t = targets?.by_brand_horizon?.[b.brand] || 0;
                const p = t > 0 ? (b.next_horizon_total / t) * 100 : null;
                return { brand: b.brand, pct: p, gap: t - b.next_horizon_total };
              })
              .filter((x) => x.pct != null && x.pct < 85 && x.gap > 0)
              .sort((a, b) => b.gap - a.gap)
              .slice(0, 3);
            return (
              <div
                className="mb-5 p-4 rounded-xl border flex items-start gap-3 flex-wrap"
                style={{ background: bg, borderColor: border }}
                data-testid="meta-risk-banner"
              >
                <div style={{ color, flexShrink: 0, marginTop: 2 }}>{icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-display font-extrabold text-base" style={{ color }}>
                      {title}
                    </h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-extrabold"
                          style={{ background: pillBg, color }}>
                      {fmt1(pct)}% cumplimiento proyectado
                    </span>
                  </div>
                  <p className="text-sm font-semibold" style={{ color }}>
                    Proyectas <strong>{fmt(totalSalesNext)} uds</strong> vs meta {targets.year} de <strong>{fmt(targets.total_horizon)} uds</strong> en los próximos {horizon} meses.
                    {(critical || (!ok && !success)) && risky.length > 0 && (
                      <>
                        {" "}Marcas críticas:{" "}
                        {risky.map((r, i) => (
                          <span key={r.brand} className="font-extrabold">
                            {r.brand} ({fmt1(r.pct)}%, gap {fmt(r.gap)})
                            {i < risky.length - 1 ? " · " : ""}
                          </span>
                        ))}
                        .
                      </>
                    )}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Top 3 commercial KPIs */}
          <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            <ExecKpi
              icon={<TrendingUp size={16}/>}
              label={`Ventas próximas · ${horizon}m`}
              value={fmt(totalSalesNext)}
              unit="unidades proyectadas"
              tip={KPI_TIPS.sales}
              forecast={salesFC}
              tone="#f59e0b"
              testid="kpi-ventas-proximas"
            />
            <LeadsNeededKpi
              needed={leadsNeededNext}
              horizon={horizon}
              perMonth={leadsNeededByMonth}
              testid="kpi-leads-necesarios"
            />
            <ExecKpi
              icon={<Zap size={16}/>}
              label="Conversión promedio"
              value={convAvg != null ? `${fmt1(convAvg)}%` : "—"}
              unit={`próximos ${horizon} meses`}
              tip={KPI_TIPS.conversion}
              forecast={convFC}
              tone="#8b5cf6"
              isRate
              testid="kpi-conversion"
            />
          </div>

          {/* Per-brand consolidation with visual coverage bar */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Package size={14} className="text-gray-600"/>
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wider text-gray-700">
                  Cobertura de Inventario por Marca
                </h3>
                <InfoTip>{KPI_TIPS.coverage}</InfoTip>
                {stockAlerts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandAlerts((v) => !v)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold transition"
                    style={{
                      background: stockAlerts.some((a) => a.severity === "critical") ? "#fee2e2" : "#fef3c7",
                      color: stockAlerts.some((a) => a.severity === "critical") ? "#991b1b" : "#92400e",
                    }}
                    data-testid="stock-alerts-badge"
                  >
                    <AlertTriangle size={11}/>
                    {stockAlerts.length} modelo{stockAlerts.length !== 1 ? "s" : ""} requiere{stockAlerts.length === 1 ? "" : "n"} reposición
                    {expandAlerts ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
                  </button>
                )}
              </div>
              <button onClick={() => setExpandBrands((v) => !v)}
                      className="text-[11px] font-extrabold text-gray-500 flex items-center gap-1 hover:text-gray-900"
                      data-testid="sarima-toggle-brands">
                {expandBrands ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                {expandBrands ? "Ver menos" : "Ver todas"}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sarima-brands-table">
                <thead>
                  <tr className="text-left text-[10px] font-extrabold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                    <th className="px-2 py-2">Marca</th>
                    <th className="px-2 py-2 text-right">Ventas {horizon}m</th>
                    {targets?.by_brand_horizon && (
                      <>
                        <th className="px-2 py-2 text-right">Meta {horizon}m</th>
                        <th className="px-2 py-2 text-right">Cumpl. %</th>
                      </>
                    )}
                    <th className="px-2 py-2 text-right">Leads necesarios</th>
                    <th className="px-2 py-2 text-right">Stock actual</th>
                    <th className="px-2 py-2" style={{ minWidth: 180 }}>Cobertura</th>
                    <th className="px-2 py-2">Riesgo</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.brands || []).slice(0, expandBrands ? undefined : 6).map((b) => {
                    const risk = RISK[b.risk] || RISK.low;
                    const leadsNeededBrand = convAvg && convAvg > 0
                      ? b.next_horizon_total / (convAvg / 100)
                      : null;
                    const brandTarget = targets?.by_brand_horizon?.[b.brand] || 0;
                    const cumplPct = brandTarget > 0
                      ? (b.next_horizon_total / brandTarget) * 100
                      : null;
                    const cumplColor = cumplPct == null ? "#9ca3af"
                      : cumplPct >= 100 ? "#059669"
                      : cumplPct >= 85 ? "#d97706"
                      : "#dc2626";
                    return (
                      <tr key={b.brand} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`sarima-brand-${b.brand}`}>
                        <td className="px-2 py-2 font-display font-extrabold">{b.brand}</td>
                        <td className="px-2 py-2 text-right font-extrabold" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {fmt(b.next_horizon_total)}
                        </td>
                        {targets?.by_brand_horizon && (
                          <>
                            <td className="px-2 py-2 text-right font-bold text-gray-700" style={{ fontVariantNumeric: "tabular-nums" }}>
                              {brandTarget > 0 ? fmt(brandTarget) : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-2 py-2 text-right font-extrabold" style={{ color: cumplColor, fontVariantNumeric: "tabular-nums" }}>
                              {cumplPct != null ? `${fmt1(cumplPct)}%` : <span className="text-gray-300">—</span>}
                            </td>
                          </>
                        )}
                        <td className="px-2 py-2 text-right font-bold text-gray-700" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {leadsNeededBrand != null ? fmt(leadsNeededBrand) : "—"}
                        </td>
                        <td className="px-2 py-2 text-right font-bold" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {fmt(b.current_stock)}
                        </td>
                        <td className="px-2 py-2">
                          <CoverageBar months={b.coverage_months} risk={b.risk}/>
                        </td>
                        <td className="px-2 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-extrabold"
                                style={{ background: risk.bg, color: risk.color }}>
                            {risk.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Alerts accordion — inline drill-down for models under threshold.
                Rows are provided by the parent so the modal stays there. */}
            {expandAlerts && stockAlerts.length > 0 && renderAlertRow && (
              <div className="mt-4 pt-4 border-t border-gray-100" data-testid="stock-alerts-inline">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={13} style={{ color: "#b45309" }}/>
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-amber-800">
                    Modelos con inventario ajustado — click para ver VINs
                  </span>
                </div>
                <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
                  {stockAlerts.map((a) => renderAlertRow(a))}
                </div>
              </div>
            )}
          </div>

          {/* Model precision — collapsed strip at the bottom */}
          {data.sarimax_summary?.enabled && (
            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center flex-wrap gap-4 text-[11px]"
                 data-testid="sarimax-precision-strip">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#8b5cf6" }}/>
                <span className="text-gray-500 font-extrabold uppercase tracking-wider text-[10px]">Precisión del modelo</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: "#fafbfc" }}>
                <span className="text-gray-500 font-bold">Margen de error:</span>
                <span className="font-extrabold text-gray-900" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {data.sarimax_summary.avg_mape_sarimax_clean != null
                    ? `${fmt1(data.sarimax_summary.avg_mape_sarimax_clean)}%`
                    : "—"}
                </span>
                <InfoTip>{KPI_TIPS.mape}</InfoTip>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: "#fafbfc" }}>
                <span className="text-gray-500 font-bold">Nivel de confianza:</span>
                <span className="font-extrabold text-gray-900">80%</span>
                <InfoTip>{KPI_TIPS.confidence}</InfoTip>
              </div>
              <div className="ml-auto text-[10px] text-gray-400 font-semibold">
                Actualizado {new Date(data.generated_at).toLocaleString("es-DO")}
                {data._cache_is_stale && (
                  <span className="ml-2 inline-flex items-center gap-1 text-amber-600 font-bold" title="La caché tenía más de 30 min. Se está recalculando en segundo plano — refresca en 30-60s para ver la nueva versión.">
                    <RefreshCw size={9} className="animate-spin"/> actualizando…
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Coverage bar — green/amber/red gradient based on months of stock. */
function CoverageBar({ months, risk }) {
  const m = Math.max(0, months || 0);
  // Map: high→red, med→amber, low→green
  const color = risk === "high" ? "#dc2626" : risk === "med" ? "#d97706" : "#16a34a";
  const bg = risk === "high" ? "#fee2e2" : risk === "med" ? "#fef3c7" : "#dcfce7";
  // Fill width: cap at 6 months. So 3m = 50%, 6m+ = 100%.
  const pct = Math.min(100, (m / 6) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 rounded-full overflow-hidden" style={{ background: bg, height: 8, minWidth: 100 }}>
        <div style={{ width: `${pct}%`, background: color, height: "100%", transition: "width .3s ease" }}/>
      </div>
      <span className="font-extrabold text-[11px]" style={{ color, fontVariantNumeric: "tabular-nums", minWidth: 32, textAlign: "right" }}>
        {m.toFixed(1)}m
      </span>
    </div>
  );
}

/** Generic KPI with mini-bar chart (numbers on top of bars) + info tooltip.
 *  Uses `flex flex-col` so the bar chart is always anchored at the bottom of
 *  the card, regardless of whether the unit text wraps to 2 lines or the
 *  optional badge is present. Keeps a row of 2-3 of these cards visually
 *  aligned even when the copy differs in length. */
function ExecKpi({ icon, label, value, unit, tip, forecast, tone, isRate, testid, badgeSlot }) {
  return (
    <div className="surface p-5 flex flex-col" data-testid={testid} style={{
      borderLeft: `4px solid ${tone}`,
      background: `linear-gradient(135deg, ${tone}05 0%, #ffffff 60%)`,
      minHeight: 232,
    }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-lg" style={{ background: `${tone}15`, color: tone, lineHeight: 0 }}>{icon}</div>
        <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">{label}</div>
        {tip && <InfoTip>{tip}</InfoTip>}
      </div>
      <div className="font-display font-extrabold mt-1" style={{ fontSize: 28, letterSpacing: "-0.02em" }}>{value}</div>
      <div className="text-[11px] font-semibold text-gray-500 mb-3 min-h-[32px] leading-4">{unit}</div>
      {badgeSlot}
      <div className="mt-auto">
        <BarsWithLabels forecast={forecast} tone={tone} isRate={isRate}/>
      </div>
    </div>
  );
}

/** Small badge that compares projected sales against the loaded annual target. */
function TargetVsForecastBadge({ target, forecast, year, testid }) {
  const pct = target > 0 ? (forecast / target) * 100 : 0;
  const ok = pct >= 100;
  const warn = pct >= 85 && pct < 100;
  const color = ok ? "#059669" : warn ? "#d97706" : "#dc2626";
  const bg = ok ? "#dcfce7" : warn ? "#fef3c7" : "#fee2e2";
  const fillPct = Math.min(100, pct);
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] mb-1">
        <span className="font-bold text-gray-600">
          Meta {year}: <span className="text-gray-900 font-extrabold">{fmt(target)} uds</span>
        </span>
        <span className="font-extrabold px-1.5 py-0.5 rounded" style={{
          color, background: bg, fontVariantNumeric: "tabular-nums", fontSize: 11,
        }}>
          {fmt1(pct)}%
        </span>
      </div>
      <div className="rounded-full overflow-hidden" style={{ background: `${bg}80`, height: 7 }}>
        <div style={{
          width: `${fillPct}%`,
          background: `linear-gradient(90deg, ${color}dd 0%, ${color} 100%)`,
          height: "100%",
          transition: "width .4s ease",
          boxShadow: `0 0 6px ${color}50`,
        }}/>
      </div>
    </div>
  );
}

/** Special "leads necesarios" KPI: matches ExecKpi structure so the bar
 *  chart aligns at the same baseline across the whole row. */
function LeadsNeededKpi({ needed, horizon, perMonth, testid }) {
  const tone = "#3b82f6";
  return (
    <div className="surface p-5 flex flex-col" data-testid={testid} style={{
      borderLeft: `4px solid ${tone}`,
      background: `linear-gradient(135deg, ${tone}05 0%, #ffffff 60%)`,
      minHeight: 232,
    }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-lg" style={{ background: `${tone}15`, color: tone, lineHeight: 0 }}>
          <Users size={16}/>
        </div>
        <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
          Leads necesarios · {horizon}m
        </div>
        <InfoTip>{KPI_TIPS.leads_needed}</InfoTip>
      </div>
      <div className="font-display font-extrabold mt-1" style={{ fontSize: 28, letterSpacing: "-0.02em" }}>
        {fmt(needed)}
      </div>
      <div className="text-[11px] font-semibold text-gray-500 mb-3 min-h-[32px] leading-4">
        para lograr las ventas proyectadas
      </div>
      <div className="mt-auto">
        <BarsWithLabels forecast={perMonth} tone={tone} isRate={false}/>
      </div>
    </div>
  );
}

/** Mini-bar chart with numeric labels ON TOP of each bar. */
function BarsWithLabels({ forecast, tone, isRate }) {
  if (!forecast || !forecast.length) {
    return <div className="text-[10px] text-gray-400 font-semibold py-3 text-center">Sin datos</div>;
  }
  const max = Math.max(...forecast.map((x) => x.mean), 1);
  return (
    <div className="flex items-end gap-1.5" style={{ height: 66 }}>
      {forecast.map((p, i) => {
        const h = Math.max(8, (p.mean / max) * 44);
        const label = isRate ? `${fmt1(p.mean)}%` : fmt(p.mean);
        return (
          <div key={`${p.year}-${p.month_idx}`} className="flex-1 flex flex-col items-center gap-1"
               title={`${p.month} ${p.year}: ${label}`}>
            <span className="text-[10px] font-extrabold" style={{ color: tone, fontVariantNumeric: "tabular-nums" }}>
              {label}
            </span>
            <div className="w-full rounded-md" style={{
              height: h, background: tone,
              opacity: 0.55 + (0.45 * (i + 1) / forecast.length),
              boxShadow: `0 1px 3px ${tone}30`,
            }}/>
            <span className="text-[10px] font-bold text-gray-600">{p.month.slice(0, 3)}</span>
          </div>
        );
      })}
    </div>
  );
}
